import mixpanel from 'mixpanel-browser';
const dayjs = require('dayjs');

if(process.env.REACT_APP_INNO_ENV === "production"){
    mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, { 
        persistence: 'localStorage',
        record_sessions_percent: 100 // Required for session recording about how much you want to record, here we set it as 100%
    });
}

// Ensure mixpanel is available globally
window.mixpanel = mixpanel; 

function propsForAllEvents({user, config, account}) {
    const topLevelKeysForAllEvents = {
      // event: 'event_name', //the name of the event (applied for each event)
      userId: `${user?.id}`,
    };

    const now = dayjs();
    const isTrialValid = account?.trialTimeEndOn && now.isBefore(dayjs(account?.trialTimeEndOn)) && now.isAfter(dayjs(account?.trialTimeStartOn));
    const isSubscriptionValid = account?.subscriptionTimeEndOn && now.isBefore(dayjs(account?.subscriptionTimeEndOn)) && now.isAfter(dayjs(account?.subscriptionTimeStartOn));

    let plan = 'none';
    if (isSubscriptionValid) {
        plan = 'paid';
    } else if (isTrialValid) {
        plan = 'trial';
    }
    let persona = 'none';
    if (user?.UserRoles && Object.keys(user.UserRoles).length){
        const matchingRole = Object.values(user.UserRoles).find(role =>
            role.AccountId === account?.id && role.UserId === user?.id
        );
        persona = matchingRole?.Persona?.name || 'none';
    }
    const propertiesForAllEvents = {
        plan,
        accountId: account?.id, //the account Id in which or for which the event was triggered, 0 for Innovare,
        accountName: account?.name, //the name of the account in which or for which the event was triggered, "Innovare" for "Innovare",
        subscription_start_date: account?.subscriptionTimeStartOn,
        subscription_end_date: account?.subscriptionTimeEndOn,  
        trial_start_on: account?.trialTimeStartOn,
        trial_end_on: account?.trialTimeEndOn,
        environment: config?.INNO_ENV, //a short name for the environment i.e. staging, production, boblocal
        source: config?.APP_URL, //the url of the environment where the event came from, i.e. https://staging3.innovaresip.com/
        userEmail: user?.email, //the email of the user who triggered this event
        userFullName: user?.fullName, //the full name of the user who triggered this event
        persona, //the persona of the user who triggered this event
        firstName: user?.firstName, //the first name of the user who triggered this event
        lastName: user?.lastName, //the last name of the user who triggered this event
    }

    return { topLevelKeysForAllEvents, propertiesForAllEvents }
}

const events = {
  ...require("./templates"),
  ...require("./registration"),
  ...require("./integrations"),
  ...require("./dashboards"),
  ...require("./userManagement"),
  ...require("./charts"),
  ...require("./accounts"),
  ...require("./users"),
  ...require("./view-filters")
};

/** A function that accepts a configuration and returns analytics tracking functions
 * @param {Object} config 
 * @param {Object} config.INNO_ENV A short name for the environment i.e. staging, production, boblocal
 * @param {Object} config.APP_URL The url of the environment where the event came from, i.e. https://staging3.innovaresip.com/
 * @param {Object} config.segment An instance of Analytics class from segment configured with this app's write key and settings
 * 
 * @returns {{track}}
**/
function configureAnalytics(config) {
    // Add Mixpanel middleware to Segment if available
    if (process.env.REACT_APP_INNO_ENV === "production" && config.segment && typeof window !== 'undefined' && window.mixpanel) {//only active in production env
        config.segment?.addSourceMiddleware(({ payload, next }) => {
            if (payload.obj.type === 'track' || payload.obj.type === 'page') {
                const segmentDeviceId = payload.obj.anonymousId;
                const userId = payload.obj.userId;

                // Simplified ID Merge (recommended for most cases)
                // window.mixpanel.register({
                //     $device_id: segmentDeviceId,
                //     distinct_id: `$device:${segmentDeviceId}`
                // });

                // Alternatively, use Original ID Merge
                window.mixpanel.register({
                    $device_id: segmentDeviceId,
                    distinct_id: userId
                });

                // Add session replay properties
                const sessionReplayProperties = window.mixpanel.get_session_recording_properties();
                payload.obj.properties = {
                    ...payload.obj.properties,
                    ...sessionReplayProperties
                };
            }

            if (payload.obj.type === 'identify') {
                const userId = payload.obj.userId;
                window.mixpanel.identify(userId);
            }

            next(payload);
        });
    }

    return {
        track: (eventName, options = {}) => {
            const { user, account } = options;

            if (events[eventName]?.excludeSuperAdmin && user?.isSuperAdmin) {
                return;
            }

            const { topLevelKeysForAllEvents, propertiesForAllEvents } = propsForAllEvents({ user, config, account });

            const trackConfig = {
                ...topLevelKeysForAllEvents,
                ...events[eventName]?.getProps(options),
                ...propertiesForAllEvents
            };

            try {
                config?.segment?.track(eventName, trackConfig);
            } catch (error) {
                console.error("Error tracking event:", error);
            }
        }
    };
}

export default configureAnalytics;
